import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

import { Typography } from '@zendesk/forethought-elements';
import { useDebouncedUpdateAbTestConfig } from './hooks';
import {
  isRecommendationApplied,
  isValidRecommendation,
} from './recommendationUtils';
import { generateAbTestConfigRequestBody } from './utils';
import {
  ABTestRecommendation,
  EndConditionType,
  GenerateVariantSummaryResponse,
  SolveABTestConfig,
  WinnerMetric,
} from 'src/services/agent-ab-test/types';
import { getCognitoToken } from 'src/utils/authUtils';
import {
  getSelectedOrganizationForSuperAdmin,
  getUserRole,
  UserRoles,
} from 'src/utils/enums';

const AB_TEST_API_URL = 'dashboard-controls/solve-ab-tests/v1';

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const bearerToken = await getCognitoToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${bearerToken}`,
    'Content-Type': 'application/json',
  };
  if (getUserRole() === UserRoles.ROLE_SUPER_ADMIN) {
    const selectedOrgId = getSelectedOrganizationForSuperAdmin()?.org_id;
    if (selectedOrgId) {
      headers['X-ORG-ID'] = selectedOrgId;
    }
  }
  return headers;
};

const parseNdjsonStream = async (
  response: Response,
  onRecommendation: (rec: ABTestRecommendation) => void,
) => {
  if (!response.body) {
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }
      try {
        const parsed = JSON.parse(line);
        if (isValidRecommendation(parsed)) {
          onRecommendation(parsed);
        }
      } catch {
        // Skip malformed lines
      }
    }
  }

  if (buffer.trim()) {
    try {
      const parsed = JSON.parse(buffer);
      if (isValidRecommendation(parsed)) {
        onRecommendation(parsed);
      }
    } catch {
      // Skip malformed final line
    }
  }
};

const applyRecommendation = (
  rec: ABTestRecommendation,
  config: SolveABTestConfig,
  handleAbTestConfigUpdate: (
    body: ReturnType<typeof generateAbTestConfigRequestBody>,
    options?: { immediate?: boolean },
  ) => void,
) => {
  switch (rec.setting_key) {
    case 'winner_metric': {
      const newMetric = rec.setting_value as WinnerMetric;
      if (!newMetric?.metric_id) {
        return;
      }
      const existing = config.winner_metrics ?? [];
      if (existing.some(m => m.metric_id === newMetric.metric_id)) {
        return;
      }
      handleAbTestConfigUpdate(
        generateAbTestConfigRequestBody(config, {
          winner_metrics: [...existing, newMetric],
        }),
        { immediate: true },
      );
      break;
    }
    case 'end_condition_type': {
      const value = rec.setting_value;
      if (value !== 'fixed_duration' && value !== 'sample_count') {
        return;
      }
      handleAbTestConfigUpdate(
        generateAbTestConfigRequestBody(config, {
          end_condition: {
            ...(config.end_condition ?? {
              end_datetime: null,
              sample_count: null,
            }),
            condition_type: value as EndConditionType,
          },
        }),
        { immediate: true },
      );
      break;
    }
    case 'duration_days': {
      const days = rec.setting_value;
      if (typeof days !== 'number' || days <= 0) {
        return;
      }
      const now = new Date();
      const startDate = config.start_datetime
        ? new Date(config.start_datetime)
        : now;
      const endDate = new Date(
        startDate.getTime() + days * 24 * 60 * 60 * 1000,
      );
      handleAbTestConfigUpdate(
        generateAbTestConfigRequestBody(config, {
          end_condition: {
            condition_type: 'fixed_duration',
            end_datetime: endDate.toISOString(),
            sample_count: null,
          },
          ...(!config.start_datetime && {
            start_datetime: now.toISOString(),
          }),
        }),
        { immediate: true },
      );
      break;
    }
    case 'sample_count': {
      const count = rec.setting_value;
      if (typeof count !== 'number' || count <= 0) {
        return;
      }
      handleAbTestConfigUpdate(
        generateAbTestConfigRequestBody(config, {
          end_condition: {
            condition_type: 'sample_count',
            end_datetime: null,
            sample_count: count,
          },
        }),
        { immediate: true },
      );
      break;
    }
  }
};

interface RecommendationsSectionProps {
  abTestConfigId: string;
  config: SolveABTestConfig;
  variantSummaries: GenerateVariantSummaryResponse[];
}

const RecommendationsSection = ({
  abTestConfigId,
  config,
  variantSummaries,
}: RecommendationsSectionProps) => {
  const { t } = useTranslation('agent-ab-test');
  const [recommendations, setRecommendations] = useState<
    ABTestRecommendation[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const { handleAbTestConfigUpdate } = useDebouncedUpdateAbTestConfig();
  const abortControllerRef = useRef<AbortController | null>(null);

  const [debouncedDescription, setDebouncedDescription] = useState(
    config.description,
  );
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedDescription(config.description),
      1000,
    );
    return () => clearTimeout(timer);
  }, [config.description]);

  const summariesKey = useMemo(
    () =>
      variantSummaries
        .map(
          s =>
            `${s.variant_name}:${s.intent_workflow_version}:${s.summary ?? ''}`,
        )
        .join(','),
    [variantSummaries],
  );

  // Streaming NDJSON requires manual fetch — RTK Query doesn't support
  // progressive state updates from ReadableStream. The abort controller
  // handles cancellation on re-fetch.
  const fetchRecommendations = useCallback(async () => {
    if (!variantSummaries.length) {
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setRecommendations([]);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}${AB_TEST_API_URL}/configs/${abTestConfigId}/recommendations`,
        {
          body: JSON.stringify({ variant_summaries: variantSummaries }),
          credentials: 'include',
          headers,
          method: 'POST',
          signal: controller.signal,
        },
      );

      if (!response.ok || !response.body) {
        throw new Error(`Failed to fetch recommendations: ${response.status}`);
      }

      await parseNdjsonStream(response, rec => {
        setRecommendations(prev => [...prev, rec]);
      });
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to fetch recommendations:', error);
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abTestConfigId, summariesKey, debouncedDescription]);

  useEffect(() => {
    fetchRecommendations();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchRecommendations]);

  if (!isLoading && recommendations.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant='font12Medium'>
        {t('recommendationsSection.heading')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          mt: '10px',
        }}
      >
        {isLoading && recommendations.length === 0 && (
          <>
            <Skeleton height={100} variant='rectangular' />
            <Skeleton height={100} variant='rectangular' />
          </>
        )}

        {recommendations.map((rec, index) => {
          const isApplied = isRecommendationApplied(rec, config);

          return (
            <Box
              key={index}
              sx={{
                backgroundImage:
                  'linear-gradient(135deg, #FFB393 0%, #ABD5FA 50%, #12A6B4 100%)',
                backgroundOrigin: 'border-box',
                border: '1.5px solid transparent',
                borderRadius: '10px',
                boxShadow: 'inset 0 100vw white',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                padding: '16px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <Typography variant='font16Bold'>
                  {rec.title.replace(/\*\*/g, '')}
                </Typography>
                <Typography color='#162040' variant='font14'>
                  {rec.description}
                </Typography>
              </Box>
              <Box
                component='button'
                disabled={isApplied}
                onClick={() =>
                  applyRecommendation(rec, config, handleAbTestConfigUpdate)
                }
                sx={{
                  '&:disabled': {
                    cursor: 'default',
                    opacity: 0.5,
                  },
                  '&:focus-visible': {
                    outline: '2px solid #12A6B4',
                    outlineOffset: '2px',
                  },
                  '&:hover:not(:disabled)': {
                    opacity: 0.8,
                  },
                  alignSelf: 'flex-start',
                  backgroundColor: '#EBF5F7',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#193D50',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  fontWeight: 600,
                  letterSpacing: '-0.1px',
                  lineHeight: '20px',
                  px: '16px',
                  py: '6px',
                }}
              >
                {isApplied
                  ? t('recommendationsSection.applied')
                  : t('recommendationsSection.apply')}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default RecommendationsSection;
